import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Breadcrumb } from 'components';
import {
    Stack,
    Snackbar,
    Grid,
    Typography,
    Alert,
    Button,
    Tooltip,
    IconButton,
    useMediaQuery,
    DialogActions,
    DialogTitle,
    DialogContent,
    Dialog,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import moment from 'moment';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import {
    viewAllClientQuote,
    GenerateReportForClient,
    updateQuoteByAction
} from 'services';
import { ButtonView } from '../../quote/components/Button';
import { CommentsGrid } from '../components/CommentsGrid';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MarkUnreadChatAltOutlinedIcon from '@mui/icons-material/MarkUnreadChatAltOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import { green } from '@mui/material/colors';
import useFetch from '../../customHooks/useFetch';
import { ClientNameDropdown } from '../custom_component/clientNameDropdown';
import { clientHasExternalPermission, clientIdsWithExternalPermission, getExternalClientPermissions } from '../../../helpers/externalClientPermissions';
import { QuoteContainerCheckoutDialog } from './QuoteContainerCheckoutDialog';
import { useCart } from '../../../CartContext';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));
export interface DialogTitleProps6 {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps6) => {
    const { children, onClose, ...other } = props;
    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

let clearStatus: any = false;

export const ViewQuoteScreen = () => {
    const [projectName, setProjectName] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [isApproveMsg, setIsApproveMsg] = useState(false);
    const [isSuccessMsg, setIsSuccessMsg] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [openComments, setOpenComments] = useState(false);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [selectedAction, setSelectedAction] = useState<any>([]);
    const [commentList, setCommentList] = useState([]);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [paginationModel, setPaginationModel] = React.useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false
    });
    const approveOptions = [
        "Test pricing was good",
        "Satisfied with the discounts",
        "Others"
    ]
    const rejectOptions = [
        "Got a better price from competitor",
        "We are seeking more discounts",
        "Project is defferred",
        "Others"
    ]
    const [selectedValues, setSelectedValues] = useState([]);
    const [comments, setComments] = useState([]);
    const [userInfo] = useFetch();
    const [selectedClientId, setSelectedClientId] = useState('');
    const { setSelectedClientId: setCartClientId } = useCart();
    const syncedCartClientId = useRef<string | null>(null);
    const [containerDialogOpen, setContainerDialogOpen] = useState(false);
    const [selectedQuoteForContainers, setSelectedQuoteForContainers] = useState<any>(null);
    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;
    const externalClientPermissions = useMemo(
        () => getExternalClientPermissions(labUserCheck?.ClientPermissions),
        [labUserCheck?.ClientPermissions]
    );
    const quoteClientIds = useMemo(
        () => clientIdsWithExternalPermission(externalClientPermissions, 'Quotes'),
        [externalClientPermissions]
    );
    // The dropdown lists every client - only the ones with Quotes permission can actually load data
    const hasQuotePermission = labUserCheck?.UserType !== 'External' ||
        clientHasExternalPermission(externalClientPermissions, selectedClientId, 'Quotes');

    useEffect(() => {
        if (!userInfo?.userMap?.length) return;

        const savedClientId = localStorage.getItem('selectedClientId');
        const savedClient = userInfo.userMap.find((client: any) => String(client.clientID) === savedClientId);
        const permittedClient = userInfo.userMap.find((client: any) => quoteClientIds.has(String(client.clientID)));
        const defaultClient = savedClient || permittedClient || userInfo.userMap[0];

        if (defaultClient) {
            setSelectedClientId(defaultClient.clientID);
        }
    }, [userInfo]);

    // Keep the checkout/cart context on the client picked here, otherwise checkout keeps using the previous client
    useEffect(() => {
        if (!selectedClientId || syncedCartClientId.current === String(selectedClientId)) return;

        const client = userInfo?.userMap?.find((item: any) => String(item.clientID) === String(selectedClientId));
        syncedCartClientId.current = String(selectedClientId);
        setCartClientId(Number(selectedClientId), client?.clientName || '');
    }, [selectedClientId, userInfo]);

    const handleCheckboxChange = (event: any) => {
        const value = event.target.name;
        setSelectedValues((prev: any) =>
            event.target.checked
                ? [...prev, value]
                : prev.filter((item) => item !== value)
        );

        if (event.target.checked && value !== "Others") {
            setComments((prev) => [...prev, value]);
        } else if (!event.target.checked && value !== "Others") {
            setComments((prev) => prev.filter((item) => item !== value));
        }
    };

    const handleOtherChange = (event: any) => {
        const value = event.target.value;
        if (selectedValues.includes("Others")) {
            setComments((prev) => {
                return prev.includes("Others") ? [...prev.filter(val => val !== "Others"), value] : [...prev, value]
            })
        }
    }

    useEffect(() => {
        if (!paginationModel.page || !selectedClientId) return;

        if (!hasQuotePermission) {
            setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
            return;
        }

        pageInitialLoad();
    }, [paginationModel.page, selectedClientId, hasQuotePermission])

    const pageInitialLoad = () => {
        let pName = clearStatus ? '' : projectName;
        let number = clearStatus ? true : quoteNumber;
        setPaginationModel(old => ({ ...old, isLoading: true }));
        viewAllClientQuote(paginationModel.page, selectedClientId, pName, number).then((res: any) => {
            if (res.status === 200) {
                if (res.data.length > 0) {
                    res.data.map((item: any) => {
                        setPaginationModel(old => ({ ...old, isLoading: false, data: res.data, total: item.totalRecordsCount }))
                    })
                } else {
                    setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
                }
            } else {
                setError(true);
                setErrorMessage(res?.data);
            }
        });
    }

    const submitForm = (e: any) => {
        clearStatus = false;
        e.preventDefault();
        e.stopPropagation();
        pageInitialLoad();
    }

    const clearTextBox = () => {
        clearStatus = true;
        setProjectName('');
        setQuoteNumber('');
        pageInitialLoad();
    }

    const handleGeneratePdf = (val: any) => {
        setDownloadSuccess(true);
        if (val?.id) {
            GenerateReportForClient(val.id, selectedClientId)
                .then((res: any) => {
                    if (res.status === 200) {
                        const linkSource = `data:application/pdf;base64,${res.data}`;
                        const downloadLink = document.createElement("a");
                        let quoteNo = val.quoteNumber;
                        const fileName = `${quoteNo}.pdf`;
                        downloadLink.href = linkSource;
                        downloadLink.download = fileName;
                        downloadLink.click();
                    } else {
                        setError(true);
                        setErrorMessage(res?.data);
                    }
                })
        }

    }

    const handleActionClick = (quoteId: any, name: any) => {
        setConfirmOpen(true);
        let obj: any = {
            "Approve": name === 'Approve',
            "QuoteId": quoteId
        }
        setSelectedAction(obj);
        setIsApproveMsg(name === 'Approve');
    }

    const dateRender = (props: GridRenderCellParams<Date>) => {
        const { row } = props;
        return (
            <div>
                {!row.createdOn ? '' : moment(row.createdOn).format("DD/MM/YYYY")}
            </div>
        );
    };

    const projectDateRender = (field: 'projectStartDate' | 'projectValidTo') => (props: any) => {
        const { row } = props;
        return (
            <div>
                {!row[field] ? '' : moment(row[field]).format("DD/MM/YYYY")}
            </div>
        );
    };

    const renderClientName = (props: any) => {
        const { row } = props;
        return (
            <div style={{ overflowX: "scroll" }}>
                <Tooltip title={row.clientName}><span>{row.clientName}</span></Tooltip>
            </div>
        )
    }

    const renderProjectName = (props: any) => {
        const { row } = props;
        return (
            <div style={{ overflowX: "scroll" }}>
                <Tooltip title={row.projectName}><span>{row.projectName}</span></Tooltip>
            </div>
        )
    }

    const renderQuoteNumber = (props: any) => {
        const { row } = props;
        return (
            <Tooltip title={row.quoteNumber}><span>{row.quoteNumber}</span></Tooltip>
        )
    }

    const renderCreatedByName = (props: any) => {
        const { row } = props;
        return (
            <Tooltip title={row.createdByName}><span>{row.createdByName}</span></Tooltip>
        )
    }

    const handleAction = (props: any) => {
        const { row } = props;
        return (
            <div style={{ width: '100%', display: 'inline-flex' }}>
                <Tooltip title="Approve">
                    <IconButton onClick={() => handleActionClick(row?.id, 'Approve')} disabled={row?.status === 3}>
                        <CheckCircleOutlineOutlinedIcon sx={{ color: (row?.status === 3) ? "#c4c7c4" : green[500] }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                    <IconButton onClick={() => handleActionClick(row?.id, 'Reject')} disabled={row?.status === 3}>
                        <CancelOutlinedIcon sx={{ color: (row?.status === 3) ? "#c4c7c4" : "#f00" }} />
                    </IconButton>
                </Tooltip>
            </div>
        );
    };

    const handleReport = (props: any) => {
        const { row } = props;
        return (<>
            <Tooltip title="Quote">
                <IconButton onClick={() => handleGeneratePdf(row)}>
                    <ArticleOutlinedIcon color="primary" />
                </IconButton>
            </Tooltip>
        </>)
    }

    const handleViewContainers = (props: any) => {
        const { row } = props;
        const externalClientPermissions = getExternalClientPermissions(labUserCheck?.ClientPermissions);
        const clientPermission = externalClientPermissions.find((client) => String(client.ClientID) === String(selectedClientId));
        const permissions = clientPermission?.Permissions || [];
        const isBottleOrdersDisabled = labUserCheck?.UserType === 'External' && !permissions.includes('BottleOrders');

        return (
            <Tooltip title={isBottleOrdersDisabled ? 'Bottle orders permission required' : 'Place An Order'}>
                <span>
                <IconButton
                    disabled={isBottleOrdersDisabled}
                    onClick={() => {
                        if (isBottleOrdersDisabled) return;

                        setSelectedQuoteForContainers(row);
                        setContainerDialogOpen(true);
                    }}
                >
                    <Inventory2OutlinedIcon sx={{ color: isBottleOrdersDisabled ? '#c4c7c4' : 'primary.main' }} />
                </IconButton>
                </span>
            </Tooltip>
        );
    }

    const handleComment = (props: any) => {
        const { row } = props;
        return (
            <>
                {row?.quotesComments?.length > 0 ? <>
                    <Tooltip title="Comments">
                        <IconButton
                            onClick={() => {
                                setOpenComments(true);
                                setCommentList(row);
                            }}
                        >
                            <MarkUnreadChatAltOutlinedIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                </> : null}
            </>
        )
    }

    const columns = [
        {
            field: 'quoteNumber',
            headerName: 'Quote Number',
            minWidth: 150,
            renderCell: renderQuoteNumber
        },
        {
            field: 'clientName',
            headerName: 'Client Name',
            minWidth: 200,
            renderCell: renderClientName
        },
        {
            field: 'projectName',
            headerName: 'Project Name',
            minWidth: 120,
            renderCell: renderProjectName
        },
        {
            field: 'versionNumber',
            headerName: 'Version',
            minWidth: 60,
            maxWidth: 60
        },
        {
            field: 'createdByName',
            headerName: 'Created by',
            minWidth: 120,
            renderCell: renderCreatedByName
        },
        {
            field: 'createdOn',
            headerName: 'Created on',
            minWidth: 100,
            renderCell: dateRender
        }, 
        {
            field: 'projectStartDate',
            headerName: 'Valid From',
            minWidth: 110,
            renderCell: projectDateRender('projectStartDate')
        },                
        {
            field: 'projectValidTo',
            headerName: 'Valid To',
            minWidth: 110,
            renderCell: projectDateRender('projectValidTo')
        },        
        {
            field: 'report',
            headerName: 'Quote',
            minWidth: 60,
            maxWidth: 60,
            renderCell: handleReport
        },       
        {
            field: 'container order',
            headerName: 'Container Order',
            minWidth: 150,
            maxWidth: 200,
            renderCell: handleViewContainers
        },
        // {
        //     field: 'action',
        //     headerName: 'Action',
        //     minWidth: 90,
        //     maxWidth: 90,
        //     renderCell: handleAction
        // },
        // {
        //     field: 'comments',
        //     headerName: '',
        //     minWidth: 60,
        //     maxWidth: 60,
        //     renderCell: handleComment
        // }
    ];

    const handleSubmit = (item: any) => {
        let str = comments.join("; ") + ".";
        setConfirmOpen(false);
        let createObj: any = {
            "Approve": item.Approve,
            "QuoteId": item.QuoteId,
            "quotesComment": {
                "quoteID": item.QuoteId,
                "comment": str
            },
            "clientId": selectedClientId
        }
        updateQuoteByAction(createObj).then((res: any) => {
            if (res.status === 200) {
                setSelectedAction({});
                setIsSuccessMsg(true);
                pageInitialLoad();
                setComments([]);
                setSelectedValues([]);
            } else {
                setError(true);
                setErrorMessage(res?.data);
            }
        });
    }

    const handleClose = () => {
        setConfirmOpen(false);
        setComments([]);
        setSelectedValues([]);
    }

    let nav: any = [{ name: 'Home', path: '/home' }, { name: 'View Quote', path: '/home/clientView' }];

    let checkboxList = selectedAction?.Approve ? approveOptions : rejectOptions;
    
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align='left' mb={2}>
                View Quotes
            </Typography>
            {userInfo ?
                <ClientNameDropdown
                    selectedClientId={selectedClientId}
                    setSelectedClientId={setSelectedClientId}
                    setPaginationModel={setPaginationModel}
                    pageName="viewQuote"
                />
                : null}
            {hasQuotePermission && <form onSubmit={(e: any) => submitForm(e)}>
                <Grid container spacing={2} mt={1} mb={5} style={{ backgroundColor: '#f5f5f5', marginLeft: '0px', padding: '16px', borderRadius: '6px', width: '100%' }}>
                    <Grid item xs={4} sm={3} md={3} style={{ padding: '0px 8px' }}>
                        <TextField
                            id="outlined-adornment-weight1"
                            sx={{ width: '100%' }}
                            type="text"
                            label="Project Name"
                            aria-describedby="outlined-weight-helper-text1"
                            value={projectName}
                            onChange={(ev: any) => setProjectName(ev.target.value)}
                            inputProps={{
                                'aria-label': 'weight',
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={4} sm={3} md={3} style={{ padding: '0px 8px' }}>
                        <TextField
                            id="outlined-adornment-weight2"
                            sx={{ width: '100%' }}
                            type="text"
                            label="Quote Number"
                            aria-describedby="outlined-weight-helper-text2"
                            value={quoteNumber}
                            onChange={(ev: any) => setQuoteNumber(ev.target.value)}
                            inputProps={{
                                'aria-label': 'weight',
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={4} sm={6} md={3} style={{ padding: '0px 8px' }}>
                        <Button variant="contained" type="submit" color="primary" startIcon={<ContentPasteSearchOutlinedIcon />}>Search</Button>&nbsp;&nbsp;
                        <Button variant="outlined" color="primary" onClick={clearTextBox} startIcon={<ClearOutlinedIcon />}>Clear All</Button>
                    </Grid>
                </Grid>
            </form>}
            {!selectedClientId ? null : !hasQuotePermission ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    You don't have permission to view quotes for this client. Please reach out to the admin to provide access for this client.
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} style={{ paddingTop: '0px' }}>
                        <DataGrid
                            autoHeight
                            disableSelectionOnClick
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#003883',
                                    color: '#fff'
                                },
                                '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                    color: '#fff'
                                },
                                '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                    color: '#fff'
                                },
                                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                                    outline: "none !important",
                                }
                            }}
                            columns={columns}
                            rows={paginationModel.data}
                            rowCount={paginationModel.total}
                            loading={paginationModel.isLoading}
                            page={paginationModel.page - 1}
                            pageSize={paginationModel.pageSize}
                            pagination
                            paginationMode="server"
                            onPageChange={(newPage: any) => {
                                setPaginationModel(old => ({ ...old, page: newPage + 1 }))
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            <BootstrapDialog
                onClose={handleClose}
                open={confirmOpen}
                fullScreen={fullScreen}
                fullWidth={true}
                sx={{ padding: 10 }}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
                    <Typography variant="h6"> {`Are you sure you want to ${selectedAction?.Approve ? 'approve' : 'reject'} this quote?`}</Typography>
                </BootstrapDialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={12} style={{ paddingTop: 0 }}>
                            <FormGroup>
                                {checkboxList.map((option) => (
                                    <FormControlLabel
                                        key={option}
                                        control={
                                            <Checkbox
                                                name={option}
                                                checked={selectedValues.includes(option)}
                                                onChange={handleCheckboxChange}
                                            />
                                        }
                                        label={option}
                                    />
                                ))}
                            </FormGroup>
                            {selectedValues.includes("Others") && (
                                <>
                                    <Typography variant="subtitle1" sx={{ paddingBottom: '10px', fontWeight: 'bold' }}>
                                        Comments
                                    </Typography>
                                    <TextField
                                        id="outlined-multiline-static"
                                        sx={{ width: "100%" }}
                                        multiline
                                        rows={4}
                                        onBlur={handleOtherChange}
                                    />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonView variant="contained" name='Yes' onClick={() => handleSubmit(selectedAction)} />
                    <ButtonView variant="outlined" name='No' onClick={handleClose} />
                </DialogActions>
            </BootstrapDialog>
            <CommentsGrid
                open={openComments}
                onClose={() => {
                    setOpenComments(false);
                }}
                commentList={commentList}
            />
            <QuoteContainerCheckoutDialog
                open={containerDialogOpen}
                quote={selectedQuoteForContainers}
                clientId={selectedClientId}
                onClose={() => {
                    setContainerDialogOpen(false);
                    setSelectedQuoteForContainers(null);
                }}
            />
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={isSuccessMsg} autoHideDuration={3000} onClose={() => setIsSuccessMsg(false)}>
                    <Alert severity="success" onClose={() => setIsSuccessMsg(false)}> {`${isApproveMsg ? 'Approved' : 'Rejected'}`} Successfully!</Alert>
                </Snackbar>
                <Snackbar open={downloadSuccess} autoHideDuration={3000} onClose={() => setDownloadSuccess(false)}>
                    <Alert severity="success" onClose={() => setDownloadSuccess(false)}>Downloading the report!</Alert>
                </Snackbar>
                <Snackbar open={error} autoHideDuration={6000} onClose={() => setError(false)}>
                    <Alert onClose={() => setError(false)} severity="warning"> {errorMessage}</Alert>
                </Snackbar>
            </Stack>
        </div >
    );
}
